import React, { useEffect } from "react";
import { X, ArrowDownLeft, ArrowUpRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatPrice } from "../hooks/formatPrice";
import { formatStatus } from "../hooks/statusTranslate";

const addBodyClass = () => document.body.classList.add("overflow-hidden");
const removeBodyClass = () => document.body.classList.remove("overflow-hidden");

const LedgerDetailSidebar = ({ isOpen, item, onClose }) => {
    const { i18n } = useTranslation();

    useEffect(() => {
        if (isOpen) {
            addBodyClass();
        } else {
            removeBodyClass();
        }
        return removeBodyClass;
    }, [isOpen]);

    if (!item) return null;

    const isCredit = item.direction?.toLowerCase() === "credit";
    const metadata = item.metadata || {};
    const hasBreakdown = Object.keys(metadata).length > 0;
    const eventName = formatStatus(item.event_type);
    const transactionId = `TRX-${String(item.id).padStart(5, "0")}`;

    const subtotal =
        Number(metadata.eligible_order_amount || 0) -
        Number(metadata.platform_commission || 0) -
        Number(metadata.stripe_fee || 0) -
        Number(metadata.shipping_label_cost || 0) -
        Number(metadata.floor_fee || 0);

    return (
        <>
            <div
                className={`ledger-sidebar-overlay ${isOpen ? "show" : ""}`}
                onClick={onClose}
            />

            <div className={`ledger-sidebar ${isOpen ? "open" : ""}`}>
                <div className="ledger-sidebar-header">
                    <h5>Ledger Detail</h5>
                    <button
                        type="button"
                        className="ledger-sidebar-close"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="ledger-sidebar-body">
                    <div className="ledger-sidebar-hero">
                        <div className="ledger-sidebar-hero-top">
                            <div className="ledger-sidebar-hero-id">
                                <div className={`ledger-icon-box ${isCredit ? "credit-bg" : "debit-bg"}`}>
                                    {isCredit ? <ArrowDownLeft size={18} /> : <ArrowUpRight size={18} />}
                                </div>
                                <div>
                                    <h4 className="ledger-sidebar-hero-title">{transactionId}</h4>
                                    <p className="ledger-sidebar-hero-subtitle">{eventName}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="ledger-sidebar-section">
                        <div className="ledger-info-list">
                            <div className="ledger-info-row">
                                <span>Order ID</span>
                                <strong>{item.order_id ? `#${item.order_id}` : "-"}</strong>
                            </div>

                            {item.seller_name && (
                                <div className="ledger-info-row">
                                    <span>Seller</span>
                                    <strong>{item.seller_name}</strong>
                                </div>
                            )}

                            <div className="ledger-info-row">
                                <span>Date</span>
                                <strong>
                                    {item.created_at ? new Date(item.created_at).toLocaleString() : "-"}
                                </strong>
                            </div>

                            <div className="ledger-info-row">
                                <span>Credit Amount</span>
                                <strong className={isCredit ? "credit-amount" : ""}>
                                    {isCredit ? formatPrice(item.amount, i18n) : "-"}
                                </strong>
                            </div>

                            <div className="ledger-info-row">
                                <span>Debit Amount</span>
                                <strong className={!isCredit ? "debit-amount" : ""}>
                                    {!isCredit ? formatPrice(item.amount, i18n) : "-"}
                                </strong>
                            </div>

                            <div className="ledger-info-row">
                                <span>Running Balance</span>
                                <strong>{formatPrice(item.balance_after, i18n)}</strong>
                            </div>

                            {item.payout_id && (
                                <div className="ledger-info-row">
                                    <span>Payout Batch</span>
                                    <strong>#{item.payout_id}</strong>
                                </div>
                            )}

                            {item.reference_id && (
                                <div className="ledger-info-row">
                                    <span>Reference</span>
                                    <strong>
                                        {item.reference_type
                                            ? `${formatStatus(item.reference_type)} #${item.reference_id}`
                                            : `#${item.reference_id}`}
                                    </strong>
                                </div>
                            )}

                            <div className="ledger-info-row">
                                <span>Created By</span>
                                <strong>{item.created_by || "System"}</strong>
                            </div>
                        </div>
                    </div>

                    {(item.reason_text || item.reason_code) && (
                        <div className="ledger-sidebar-section">
                            <h6 className="ledger-sidebar-section-title">Reason</h6>
                            <p className="ledger-sidebar-description">
                                {item.reason_text || formatStatus(item.reason_code)}
                            </p>
                        </div>
                    )}

                    {hasBreakdown && (
                        <div className="ledger-sidebar-section">
                            <h6 className="ledger-sidebar-section-title">Order Financial Breakdown</h6>
                            <div className="ledger-info-list">
                                {metadata.eligible_order_amount !== undefined && (
                                    <div className="ledger-info-row">
                                        <span>Order Amount</span>
                                        <strong>{formatPrice(metadata.eligible_order_amount || 0, i18n)}</strong>
                                    </div>
                                )}

                                {metadata.platform_commission !== undefined && (
                                    <div className="ledger-info-row">
                                        <span>Platform Commission</span>
                                        <strong className="debit-amount">
                                            -{formatPrice(metadata.platform_commission || 0, i18n)}
                                        </strong>
                                    </div>
                                )}

                                {metadata.commission_ex_vat !== undefined && (
                                    <div className="ledger-info-row ledger-info-row-sub">
                                        <span>Commission Ex VAT</span>
                                        <strong>{formatPrice(metadata.commission_ex_vat || 0, i18n)}</strong>
                                    </div>
                                )}

                                {metadata.commission_vat !== undefined && (
                                    <div className="ledger-info-row ledger-info-row-sub">
                                        <span>Commission VAT</span>
                                        <strong>{formatPrice(metadata.commission_vat || 0, i18n)}</strong>
                                    </div>
                                )}

                                {!!metadata.stripe_fee && (
                                    <div className="ledger-info-row">
                                        <span>Stripe Fee</span>
                                        <strong className="debit-amount">
                                            -{formatPrice(metadata.stripe_fee || 0, i18n)}
                                        </strong>
                                    </div>
                                )}

                                {!!metadata.floor_fee && (
                                    <div className="ledger-info-row">
                                        <span>Floor Fee</span>
                                        <strong className="debit-amount">
                                            -{formatPrice(metadata.floor_fee || 0, i18n)}
                                        </strong>
                                    </div>
                                )}

                                {!!metadata.shipping_label_cost && (
                                    <div className="ledger-info-row">
                                        <span>Shipping Label Cost</span>
                                        <strong className="debit-amount">
                                            -{formatPrice(metadata.shipping_label_cost || 0, i18n)}
                                        </strong>
                                    </div>
                                )}

                                <div className="ledger-info-row ledger-info-row-strong">
                                    <span>Subtotal</span>
                                    <strong>{formatPrice(subtotal, i18n)}</strong>
                                </div>

                                {!!metadata.negative_balance_applied && (
                                    <div className="ledger-info-row">
                                        <span>Outstanding Balance Applied</span>
                                        <strong className="debit-amount">
                                            -{formatPrice(metadata.negative_balance_applied || 0, i18n)}
                                        </strong>
                                    </div>
                                )}

                                {metadata.final_payout !== undefined && (
                                    <div className="ledger-info-row ledger-info-row-highlight">
                                        <span>You Received</span>
                                        <strong className="credit-amount">
                                            {formatPrice(metadata.final_payout || 0, i18n)}
                                        </strong>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default LedgerDetailSidebar;
